/**
 * Non-reactive client for the bug-report system.
 *
 * Why this exists alongside the React hooks (`hooks/useBugReports.js`):
 *  - The DevTools verification helpers need to call authed mutations from
 *    outside the React tree.
 *  - Session 3's bulk-export flow makes a series of one-shot fetches +
 *    screenshot downloads + zip-build steps; reactivity adds nothing and
 *    mid-export re-renders would only cause confusion.
 *  - The driver's ConvexClient (createConvexDriver) is intentionally
 *    unauthenticated; it can't call auth-gated functions. This client
 *    pulls the JWT from localStorage the same way `getAuthedConvexClient`
 *    in useStorage.js does, then uses it for one-shot HTTP calls.
 *
 * Usage:
 *   const client = await getAuthedBugReportClient();
 *   await client.submit({ data, screenshotStorageIds: [] });
 *   const url = await client.uploadScreenshot(blob);
 *
 * Demo mode (`?local=1`): there's no JWT in localStorage; the constructor
 * throws "Not authenticated". Callers should gate on auth state before
 * invoking. The Phase 2 floating button is hidden in demo for this reason.
 *
 * String-based function references are used (`'bugReports:submit'`) instead
 * of `api.bugReports.submit` so this file compiles cleanly even before
 * `npx convex dev` regenerates `_generated/api.d.ts` with the new module.
 * Convex's `anyApi` runtime resolves both forms identically.
 */

const REPORT_ID_PREFIX = 'rpt';

const loadHttpClient = async () => {
  const url = process.env.REACT_APP_CONVEX_URL;
  if (!url) throw new Error('REACT_APP_CONVEX_URL not set');
  const { ConvexHttpClient } = await import('convex/browser');
  const rawTokenKey = Object.keys(window.localStorage).find(k => /__convexAuthJWT/.test(k));
  if (!rawTokenKey) throw new Error('Not authenticated — sign in with Google first.');
  const token = window.localStorage.getItem(rawTokenKey);
  if (!token) throw new Error('Auth token missing.');
  const client = new ConvexHttpClient(url);
  client.setAuth(token);
  return client;
};

// Cached so a sequence of bulk-export calls reuses one client. Reset on
// auth state change is handled implicitly: a fresh sign-in writes a new
// token to localStorage; the next caller picks it up via a new instance
// (we always re-read on getAuthedBugReportClient call).
const getRawClient = () => loadHttpClient();

/**
 * Generate a stable, sortable, unique-enough report id. Convex auto-generates
 * `_id` per row, but we want a human-readable, client-known id (so the UI
 * can route to the new report immediately, attach screenshots before the
 * mutation returns, etc.).
 */
export const generateReportId = () => {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${REPORT_ID_PREFIX}-${ts}-${rand}`;
};

/**
 * Construct an authed bug-report client. Call this fresh for each user
 * action — caching the client across long-running flows risks staleness
 * if the user signs out / back in mid-session.
 *
 * @returns {Promise<BugReportClient>}
 */
export const getAuthedBugReportClient = async () => {
  const client = await getRawClient();
  return {
    /**
     * Upload a screenshot Blob and return its storageId. The storageId can
     * then be passed to submit() or attachScreenshot().
     */
    async uploadScreenshot(blob) {
      const uploadUrl = await client.mutation('bugReports:generateScreenshotUploadUrl', {});
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': blob.type || 'image/png' },
        body: blob,
      });
      if (!res.ok) {
        throw new Error(`Screenshot upload failed: ${res.status} ${res.statusText}`);
      }
      const json = await res.json();
      return json.storageId;
    },

    /**
     * Submit a new report. Caller is responsible for stringifying `data`
     * (see schema.ts for the JSON shape). Caller-supplied reportId so the
     * UI can navigate to the thread immediately.
     */
    async submit({ reportId, data, screenshotStorageIds = [] }) {
      return await client.mutation('bugReports:submit', {
        reportId: reportId || generateReportId(),
        data,
        screenshotStorageIds,
      });
    },

    async addComment(reportId, body) {
      return await client.mutation('bugReports:addComment', { reportId, body });
    },

    async updateStatus(reportId, status) {
      return await client.mutation('bugReports:updateStatus', { reportId, status });
    },

    async markRead(reportId) {
      return await client.mutation('bugReports:markRead', { reportId });
    },

    async markBulkExported(ts) {
      return await client.mutation('bugReports:markBulkExported', { ts });
    },

    async list({ paginationOpts, status, mine }) {
      return await client.query('bugReports:list', {
        paginationOpts,
        ...(status ? { status } : {}),
        ...(mine ? { mine } : {}),
      });
    },

    async listSinceLastExport({ paginationOpts, since }) {
      return await client.query('bugReports:listSinceLastExport', {
        paginationOpts,
        since,
      });
    },

    async getById(reportId) {
      return await client.query('bugReports:getById', { reportId });
    },

    async leaderboard(limit) {
      return await client.query('bugReports:leaderboard', limit ? { limit } : {});
    },

    async getBulkExport(reportIds) {
      return await client.query('bugReports:getBulkExport', { reportIds });
    },

    async getLastBulkExportAt() {
      return await client.query('settings:getLastBulkExportAt', {});
    },
  };
};

/**
 * Helper for the "since last export" flow: paginates listSinceLastExport
 * until isDone, returns all rows. Beta scale; if export volume grows past
 * a few hundred per cycle, switch to streaming.
 *
 * @param {object} opts
 * @param {number} opts.since   Epoch ms bookmark from settings.lastBulkExportAt.
 * @param {number} [opts.batchSize=50]
 */
export const fetchAllReportsSinceLastExport = async ({ since, batchSize = 50 }) => {
  const client = await getAuthedBugReportClient();
  /** @type {any[]} */
  const out = [];
  let cursor = null;
  while (true) {
    const page = await client.listSinceLastExport({
      paginationOpts: { numItems: batchSize, cursor },
      since,
    });
    out.push(...page.page);
    if (page.isDone) break;
    cursor = page.continueCursor;
  }
  return out;
};
